import { useCallback, useEffect, useRef, useState } from "react";
import type { FriendsChanged } from "@mk/shared";
import { RematchBanner } from "./components/RematchBanner.tsx";
import { InviteNotifications } from "./components/InviteNotifications.tsx";
import { attachDuelListeners, useDuelStore } from "./features/duel/duelStore.ts";
import type { BackHandler, RegisterBackHandler, Screen } from "./lib/navigation.ts";
import { useSession } from "./lib/session.ts";
import { disconnectSocket, getSocket } from "./lib/socket.ts";
import { installButtonSounds, playSound } from "./lib/sound.ts";
import { isFriendNotification } from "./lib/notificationSounds.ts";
import { AuthScreen } from "./screens/AuthScreen.tsx";
import { DailyScreen } from "./screens/DailyScreen.tsx";
import { DuelScreen } from "./screens/DuelScreen.tsx";
import { FriendsScreen } from "./screens/FriendsScreen.tsx";
import { HistoryScreen } from "./screens/HistoryScreen.tsx";
import { HomeScreen } from "./screens/HomeScreen.tsx";
import { PracticeScreen } from "./screens/PracticeScreen.tsx";
import { SpectateScreen } from "./screens/SpectateScreen.tsx";
import { ProfileScreen } from "./screens/ProfileScreen.tsx";
import { StatisticsScreen } from "./screens/StatisticsScreen.tsx";
import { LeaderboardScreen } from "./screens/LeaderboardScreen.tsx";
import { messages } from "./i18n/index.ts";
import { attachTeamListeners, useTeamStore } from "./features/team/teamStore.ts";
import { TeamScreen } from "./screens/TeamScreen.tsx";
import { readTeamInviteCode } from "./features/team/inviteLink.ts";
import { APP_EDITION } from "./lib/edition.ts";
import { RaceScreen } from "./screens/RaceScreen.tsx";
import { attachRaceListeners, useRaceStore } from "./features/race/raceStore.ts";
import { configureAds } from "./lib/adGate.ts";

const NEEDS_ACCOUNT: Screen[] = ["daily", "duel", "race", "team", "friends", "profile", "spectate", "leaderboard"];
const HISTORY_KEY = "mkScreen";

const SCREENS: Screen[] = [
  "home",
  "auth",
  "practice",
  "practice-history",
  "daily",
  "duel",
  "race",
  "team",
  "friends",
  "history",
  "statistics",
  "leaderboard",
  "profile",
  "spectate",
];

function screenFromHistory(state: unknown): Screen {
  if (!state || typeof state !== "object") return "home";
  const value = (state as Record<string, unknown>)[HISTORY_KEY];
  return typeof value === "string" && SCREENS.includes(value as Screen)
    ? (value as Screen)
    : "home";
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const [authReason, setAuthReason] = useState<string | undefined>(undefined);
  /** İzlenen maç. Ekran değişince temizlenmiyor; geri gelince aynı maç açılır. */
  const [spectateMatchId, setSpectateMatchId] = useState<string | null>(null);
  const status = useSession((s) => s.status);
  const userId = useSession((s) => s.user?.id);
  const removeAds = useSession((s) => s.user?.removeAds);
  const bootstrap = useSession((s) => s.bootstrap);
  const duelPhase = useDuelStore((s) => s.phase);
  const teamPhase = useTeamStore((s) => s.phase);
  const racePhase = useRaceStore((s) => s.phase);
  const screenRef = useRef<Screen>("home");
  const backHandlerRef = useRef<BackHandler | null>(null);
  const bypassBackRef = useRef(false);

  const showScreen = useCallback((next: Screen, mode: "push" | "replace" = "push") => {
    backHandlerRef.current = null;
    screenRef.current = next;
    setScreen(next);
    const state = { [HISTORY_KEY]: next };
    if (mode === "replace") window.history.replaceState(state, "");
    else window.history.pushState(state, "");
  }, []);

  /** Adresteki `?takim=KOD` — açılışta bir kez okunur. */
  const [pendingInvite, setPendingInvite] = useState(() => readTeamInviteCode(window.location.search, APP_EDITION));
  const invitePromptedRef = useRef(false);

  // Ana sayfadaki "Rakip bul" düello ekranını açıp aramayı da başlatır.
  const [duelAutoSearch, setDuelAutoSearch] = useState(false);

  const registerBackHandler = useCallback<RegisterBackHandler>((handler) => {
    backHandlerRef.current = handler;
    return () => {
      if (backHandlerRef.current === handler) backHandlerRef.current = null;
    };
  }, []);

  const requestBack = useCallback(() => window.history.back(), []);
  const completeBack = useCallback(() => {
    bypassBackRef.current = true;
    window.history.back();
  }, []);
  const followSpectateRematch = useCallback((matchId: string) => {
    // Aynı izleme ekranında kal: tarayıcı geçmişine yeni bir ekran ekleme.
    setSpectateMatchId(matchId);
  }, []);

  useEffect(() => {
    // Davet kodu okunduysa adresten sil: yenilemede tekrar katılmaya çalışmasın.
    const cleanUrl = readTeamInviteCode(window.location.search, APP_EDITION)
      ? window.location.pathname + window.location.hash
      : undefined;
    window.history.replaceState({ [HISTORY_KEY]: "home" }, "", cleanUrl);
    const onPopState = (event: PopStateEvent) => {
      const target = screenFromHistory(event.state);
      if (bypassBackRef.current) {
        bypassBackRef.current = false;
        backHandlerRef.current = null;
        screenRef.current = target;
        setScreen(target);
        return;
      }

      const disposition = backHandlerRef.current?.() ?? "allow";
      if (disposition === "block") {
        window.history.pushState({ [HISTORY_KEY]: screenRef.current }, "");
        return;
      }
      if (disposition === "consume") return;

      backHandlerRef.current = null;
      screenRef.current = target;
      setScreen(target);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => installButtonSounds(), []);

  useEffect(() => { void configureAds(); }, [status, removeAds]);

  /*
   * Soket ve düello dinleyicileri oturum boyunca ayakta durur — ekrana bağlı
   * değil. Arkadaşın meydan okumasını kabul ettiğinde maç anında başlıyor;
   * dinleyici yalnızca düello ekranında olsaydı o an kaçırılırdı.
   */
  useEffect(() => {
    if (status !== "authed") {
      disconnectSocket();
      return;
    }
    const detachDuel = attachDuelListeners();
    const detachTeam = attachTeamListeners();
    const detachRace = attachRaceListeners();
    return () => {
      detachDuel();
      detachTeam();
      detachRace();
    };
  }, [status]);

  useEffect(() => {
    if (status !== "authed" || !userId) return;
    const socket = getSocket();
    const onFriendsChanged = (change: FriendsChanged) => {
      if (isFriendNotification(change, userId)) playSound("notification");
    };
    const onIncomingChallenge = () => playSound("notification");
    const onIncomingTeamInvite = () => playSound("notification");
    const onRematchOffer = () => playSound("notification");

    socket.on("friends:changed", onFriendsChanged);
    socket.on("challenge:incoming", onIncomingChallenge);
    socket.on("team:invite:incoming", onIncomingTeamInvite);
    socket.on("rematch:offered", onRematchOffer);
    return () => {
      socket.off("friends:changed", onFriendsChanged);
      socket.off("challenge:incoming", onIncomingChallenge);
      socket.off("team:invite:incoming", onIncomingTeamInvite);
      socket.off("rematch:offered", onRematchOffer);
    };
  }, [status, userId]);

  // Maç başladığında oyuncuyu tahtaya al — hangi ekranda olursa olsun.
  useEffect(() => {
    if (duelPhase === "countdown" || duelPhase === "live") {
      if (screenRef.current === "duel") return;
      showScreen("duel", screenRef.current === "home" ? "push" : "replace");
    }
  }, [duelPhase, showScreen]);

  useEffect(() => {
    if (["lobby", "countdown", "live", "over"].includes(teamPhase)) {
      if (screenRef.current === "team") return;
      showScreen("team", screenRef.current === "home" ? "push" : "replace");
    }
  }, [teamPhase, showScreen]);

  useEffect(() => {
    if (racePhase === "countdown" || racePhase === "live") {
      if (screenRef.current === "race") return;
      showScreen("race", screenRef.current === "home" ? "push" : "replace");
    }
  }, [racePhase, showScreen]);

  // Davet bağlantısı: giriş yapılmışsa 2v2 ekranına geç; yapılmamışsa bir kez giriş iste.
  useEffect(() => {
    if (!pendingInvite || status === "loading") return;
    if (status === "authed") {
      if (screen !== "auth" && screen !== "team") showScreen("team");
      return;
    }
    if (screen !== "home") return;
    if (invitePromptedRef.current) {
      // Giriş ekranından vazgeçildi; daveti bırak.
      setPendingInvite(null);
      return;
    }
    invitePromptedRef.current = true;
    setAuthReason(messages.team.inviteSignIn);
    showScreen("auth");
  }, [pendingInvite, screen, showScreen, status]);

  // Oturum düşerse hesap gerektiren ekranlarda kalınmasın.
  useEffect(() => {
    if (status === "anon" && NEEDS_ACCOUNT.includes(screen)) showScreen("home", "replace");
  }, [status, screen, showScreen]);

  function currentScreen() {
    switch (screen) {
      case "auth":
        return <AuthScreen onBack={requestBack} reason={authReason} />;
      case "practice":
        return (
          <PracticeScreen
            onBack={requestBack}
            onExitConfirmed={completeBack}
            onSignIn={(reason) => {
              setAuthReason(reason);
              showScreen("auth");
            }}
            registerBackHandler={registerBackHandler}
          />
        );
      case "practice-history":
        return (
          <HistoryScreen
            initialTab="solo"
            onBack={requestBack}
            onSignIn={(reason) => {
              setAuthReason(reason);
              showScreen("auth");
            }}
            registerBackHandler={registerBackHandler}
          />
        );
      case "daily":
        return (
          <DailyScreen
            onBack={requestBack}
            onExitConfirmed={completeBack}
            registerBackHandler={registerBackHandler}
          />
        );
      case "duel":
        return (
          <DuelScreen
            onBack={requestBack}
            onExitConfirmed={completeBack}
            registerBackHandler={registerBackHandler}
            onFriends={() => showScreen("friends")}
            autoSearch={duelAutoSearch}
            onAutoSearchHandled={() => setDuelAutoSearch(false)}
          />
        );
      case "team":
        return (
          <TeamScreen
            onBack={requestBack}
            registerBackHandler={registerBackHandler}
            inviteCode={pendingInvite}
            onInviteHandled={() => setPendingInvite(null)}
          />
        );
      case "race":
        return <RaceScreen onBack={requestBack} onExitConfirmed={completeBack} registerBackHandler={registerBackHandler} />;
      case "friends":
        return (
          <FriendsScreen
            onBack={requestBack}
            onDuel={() => showScreen("duel", "replace")}
            onTeam={() => showScreen("team", "replace")}
            onRace={() => showScreen("race", "replace")}
            registerBackHandler={registerBackHandler}
            onSpectate={(matchId) => {
              setSpectateMatchId(matchId);
              showScreen("spectate");
            }}
          />
        );
      case "spectate":
        return spectateMatchId ? (
          <SpectateScreen
            matchId={spectateMatchId}
            onBack={requestBack}
            onFollowMatch={followSpectateRematch}
          />
        ) : (
          <FriendsScreen onBack={requestBack} onDuel={() => showScreen("duel", "replace")} onTeam={() => showScreen("team", "replace")} />
        );
      case "history":
        return (
          <HistoryScreen
            onBack={requestBack}
            onSignIn={(reason) => {
              setAuthReason(reason);
              showScreen("auth");
            }}
            registerBackHandler={registerBackHandler}
          />
        );
      case "profile":
        return <ProfileScreen onBack={requestBack} registerBackHandler={registerBackHandler} />;
      case "statistics":
        return <StatisticsScreen onBack={requestBack} onSignIn={(reason) => {
          setAuthReason(reason);
          showScreen("auth");
        }} />;
      case "leaderboard":
        return <LeaderboardScreen onBack={requestBack} />;
      default:
        return (
          <HomeScreen
            onNavigate={showScreen}
            onFindOpponent={() => {
              setDuelAutoSearch(true);
              showScreen("duel");
            }}
            registerBackHandler={registerBackHandler}
            onSignIn={(reason) => {
              setAuthReason(reason);
              showScreen("auth");
            }}
          />
        );
    }
  }

  if (status === "loading") {
    return (
      <div className="app grid place-items-center">
        <p className="text-sm text-muted">{messages.common.loading}</p>
      </div>
    );
  }

  return (
    <>
      {currentScreen()}
      <InviteNotifications
        screen={screen}
        authed={status === "authed"}
        onDuel={() => showScreen("duel", "replace")}
        onRace={() => showScreen("race", "replace")}
        onTeam={() => showScreen("team", "replace")}
        onOpenAll={() => showScreen("friends")}
      />
      {/*
       * Rövanş teklifi her ekranın üstünde. Teklif oturum boyunca dinleniyor
       * ama yalnızca sonuç panelinde çiziliyordu: paneli kapatmış oyuncu
       * teklifi hiç görmüyor, karşı taraf boşuna bekliyordu.
       */}
      <RematchBanner />
    </>
  );
}
